import { Text } from "@modules/common/components/ui"
import { getProductPrice } from "@lib/util/get-product-price"
import {
  getProductBrand,
  getProductCode,
  getProductModel,
  getProductSpecChips,
  getProductSummary,
} from "@lib/util/product-specs"
import {
  getListingStock,
  getUsedConditionKey,
  getUsedConditionLabel,
  getUsedSpecs,
  isUsedListing,
  isUsedSold,
} from "@lib/util/used-listing"
import { HttpTypes } from "@medusajs/types"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import Thumbnail from "../thumbnail"
import PreviewPrice from "./price"

function SpecIcon({ type }: { type: string }) {
  if (type === "poe") {
    return (
      <svg viewBox="0 0 16 16" aria-hidden className="h-3.5 w-3.5">
        <path
          d="M8 1 3 9h4l-1 6 7-9H9l1-5Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
      </svg>
    )
  }

  if (type === "sfp") {
    return (
      <svg viewBox="0 0 16 16" aria-hidden className="h-3.5 w-3.5">
        <path
          d="M2 5h12v6H2zM5 5V3h6v2M5 11v2m6-2v2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
      </svg>
    )
  }

  if (type === "ros") {
    return (
      <svg viewBox="0 0 16 16" aria-hidden className="h-3.5 w-3.5">
        <path
          d="M3 4h10M3 8h10M3 12h6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
        />
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 16 16" aria-hidden className="h-3.5 w-3.5">
      <path
        d="M4 4h8v8H4zM2 6h2m8 0h2M2 10h2m8 0h2M6 2v2m4-2v2M6 12v2m4-2v2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  )
}

export default function ProductPreview({
  product,
  isFeatured,
  region: _region,
  tone = "light",
}: {
  product: HttpTypes.StoreProduct
  isFeatured?: boolean
  region: HttpTypes.StoreRegion
  tone?: "light" | "dark"
}) {
  const { cheapestPrice } = getProductPrice({
    product,
  })
  const brand = getProductBrand(product)
  const model = getProductModel(product)
  const code = getProductCode(product)
  const summary = getProductSummary(product)
  const used = isUsedListing(product)
  const sold = isUsedSold(product)
  const stock = used ? getListingStock(product) : null
  const conditionKey = used ? getUsedConditionKey(product) : ""
  const conditionLabel = conditionKey ? getUsedConditionLabel(conditionKey) : ""
  const usedSpecs = used ? getUsedSpecs(product).slice(0, 2) : []
  const chips = used ? [] : getProductSpecChips(product).slice(0, 2)
  const isDark = tone === "dark"
  const subtitle = code || (brand ? brand : "")

  return (
    <LocalizedClientLink
      href={`/products/${product.handle}`}
      className="group block h-full"
    >
      <article
        className={isDark ? "vx-product-card" : "ui-product-card"}
        data-testid="product-wrapper"
      >
        <div className="relative">
          <Thumbnail
            thumbnail={product.thumbnail}
            images={product.images}
            size="square"
            isFeatured={isFeatured}
            className={
              isDark
                ? "aspect-[4/3] rounded-none border-0 border-b border-vx-line bg-vx-elevated shadow-none"
                : "ui-product-media"
            }
            imageClassName={`object-contain p-6 transition-transform duration-300 ease-out group-hover:scale-[1.04] ${sold ? "opacity-50" : ""}`}
          />
          {used && (
            <span className="absolute left-3 top-3 rounded-full bg-mt-ink px-2.5 py-1 text-[11px] font-semibold text-white">
              {sold ? "ขายแล้ว" : "มือสอง"}
            </span>
          )}
        </div>

        <div className="flex flex-1 flex-col gap-3 p-4 small:p-5">
          {(brand || conditionLabel) && (
            <span
              className={
                isDark
                  ? "text-[11px] font-medium uppercase tracking-[0.08em] text-vx-accent"
                  : "text-[11px] font-medium uppercase tracking-[0.08em] text-[#006fff]"
              }
            >
              {[brand, conditionLabel].filter(Boolean).join(" · ")}
            </span>
          )}

          <div>
            <Text
              className={
                isDark
                  ? "text-[17px] font-semibold leading-6 text-vx-ink"
                  : "text-[17px] font-semibold leading-6 text-mt-ink"
              }
              data-testid="product-title"
            >
              {model}
            </Text>
            {subtitle && subtitle !== model && (
              <p
                className={
                  isDark
                    ? "mt-1 text-[13px] leading-5 text-vx-muted"
                    : "mt-1 text-[13px] leading-5 text-mt-muted"
                }
              >
                {subtitle}
              </p>
            )}
          </div>

          {!used && summary && (
            <p
              className={
                isDark
                  ? "line-clamp-2 text-[13px] leading-5 text-vx-muted"
                  : "line-clamp-2 text-[13px] leading-5 text-mt-muted"
              }
            >
              {summary}
            </p>
          )}

          {usedSpecs.length > 0 && (
            <ul className="flex flex-col gap-1.5">
              {usedSpecs.map((spec) => (
                <li
                  key={spec}
                  className={
                    isDark
                      ? "text-[12px] leading-4 text-vx-muted"
                      : "text-[12px] leading-4 text-mt-muted"
                  }
                >
                  {spec}
                </li>
              ))}
            </ul>
          )}

          {typeof stock === "number" && stock > 1 && (
            <p
              className={
                isDark
                  ? "text-[12px] font-medium text-vx-ink"
                  : "text-[12px] font-medium text-mt-ink"
              }
            >
              เหลือ {stock} ชิ้น
            </p>
          )}

          {chips.length > 0 && (
            <ul className="flex flex-col gap-1.5">
              {chips.map((chip) => (
                <li
                  key={`${chip.key}-${chip.value}`}
                  className={
                    isDark
                      ? "flex items-center gap-1.5 text-[12px] leading-4 text-vx-muted"
                      : "flex items-center gap-1.5 text-[12px] leading-4 text-mt-muted"
                  }
                >
                  <SpecIcon type={chip.key} />
                  <span>{chip.value}</span>
                </li>
              ))}
            </ul>
          )}

          <div
            className={
              isDark
                ? "mt-1 text-[18px] font-semibold tracking-tight text-vx-ink"
                : "mt-1 text-[18px] font-semibold tracking-tight text-mt-ink"
            }
          >
            {cheapestPrice && <PreviewPrice price={cheapestPrice} />}
          </div>

          <span className={isDark ? "ui-product-cta-dark" : "ui-product-cta"}>
            {sold ? "ขายแล้ว" : used ? "ดูรายละเอียด" : "View details"}
          </span>
        </div>
      </article>
    </LocalizedClientLink>
  )
}
